import React, { useState, useMemo } from 'react';
import { extractConnectorPower } from './utils/powerUtils';
import { buildPolylineCumulative, computeChargerRouteDistance, parseNumericKw, clusterChargers, selectPrimaryCharger, haversineKm } from './utils/distanceUtils';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Avatar,
  Switch,
  Divider,
  Paper,
  LinearProgress,
  Collapse
} from '@mui/material';
import {
  Close as CloseIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Share as ShareIcon,
  Navigation as NavigationIcon,
  Bolt as BoltIcon,
  Route as RouteIcon,
  Timer as TimerIcon,
  BatteryFull as BatteryIcon,
  Battery80 as Battery80Icon,
  ElectricCar as ElectricCarIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { alpha, useTheme } from '@mui/material/styles';

// (safeToString removed - no longer needed; we rely on direct coercion)

// Enhanced network icon helper function with comprehensive mapping
const getNetworkIcon = (charger) => {
  if (!charger) return "/assets/networkFilter/Generic.svg";

  // Get possible network fields
  const possibleNetworkFields = [
    charger?.name,
    charger?.operator,
    charger?.chargepoint_name,
    charger?.provider,
    charger?.owner,
    charger?.network,
    charger?.team_name,
  ].filter(Boolean);

  // Comprehensive network mapping with exact matches
  const networkMapping = {
    // Indian Networks
    'zynka': 'Zynka.png',
    'statiq': 'Statiq.svg',
    'tata': 'Tata.svg',
    'ather': 'Ather.svg',
    'iocl': 'IOCL.svg',
    'bpcl': 'BPCL.svg',
    'hp': 'HP.svg',
    'jio bp': 'JioBP.svg',
    'jiobp': 'JioBP.svg',
    'volttic': 'Volttic.svg',
    'zeon': 'Zeon.svg',
    'relux': 'Relux.svg',
    'chargezone': 'ChargeZone.svg',
    'charge zone': 'ChargeZone.svg',
    'chargegrid': 'ChargeGrid.svg',
    'charge grid': 'ChargeGrid.svg',
    'ola': 'Ola.svg',
    'eesl': 'EESL.svg',
    'ntpc': 'NTPC.svg',
    'pgcil': 'PGCIL.svg',
    'reil': 'REIL.svg',
    'tcil': 'TCIL.svg',
    'becil': 'BECIL.svg',
    'bbmp': 'BBMP.svg',
    'delta': 'Delta.svg',
    'efill': 'Efill.svg',
    'electriva': 'Electriva.svg',
    'evre': 'EVRe.svg',
    'fortum': 'Fortum.svg',
    'goego': 'Goego.svg',
    'kazam': 'Kazam.svg',
    'kht': 'KHT.svg',
    'kropex': 'Kropex.svg',
    'minooki': 'Minooki.svg',
    'oem': 'OEM.svg',
    'plugzmart': 'PlugzMart.svg',
    'verde': 'verde.svg',
    'xobolt': 'XObolt.svg',
    'aargo': 'AARgo.svg',
    
    // International Networks
    'tesla': 'Tesla Public Supercharger.png',
    'fastned': 'Fastned.png',
    'ionity': 'Ionity.png',
    'chargepoint': 'ChargePoint.png',
    'blink': 'Blink.png',
    'bp pulse': 'BP Pulse.png',
    'shell recharge': 'Community by Shell Recharge.png',
    'instavolt': 'InstaVolt.png',
    'pod point': 'Pod Point.png',
    'osprey': 'Osprey.png',
    'geniepoint': 'GeniePoint.png',
    'gridserve': 'GRIDSERVE Electric Highway.png',
    'mer': 'Mer.png',
    'mfg': 'MFG EV Power.png',
    'monta': 'Monta.png',
    'pogo': 'PoGo.png',
    'raw charging': 'RAW Charging.png',
    'scottishpower': 'ScottishPower Recharge.png',
    'source london': 'Source London.png',
    'ubitricity': 'SR Ubitricity .png',
    'vendelectric': 'VendElectric.png',
    'zapmap': 'ZAPMAP.png',
    'zeronet': 'Zeronet.png',
    'zest': 'Zest.png',
    'allego': 'Allego.png',
    'apcoa': 'APCOA.png',
    'alfa power': 'Alfa Power.png',
    'chargy': 'chargy.png',
    'chargemy street': 'ChargeMyStreet.png',
    'energise': 'Energise.png',
    'eo charging': 'EO Charging.png',
    'esb energy': 'ESB Energy.png',
    'evolt': 'Evolt Network.png',
    'fuuse': 'Fuuse.png',
    'lidl': 'Lidl.png',
    'justcharge': 'JustCharge by JustPark.png',
    
    // Fallbacks
    'generic': 'Generic.svg',
    'other': 'Generic.svg'
  };

  // Try to match network name
  let detectedNetwork = 'generic';
  for (const networkField of possibleNetworkFields) {
    const normalizedField = networkField.toLowerCase().trim();
    
    // Direct mapping check
    if (networkMapping[normalizedField]) {
      detectedNetwork = normalizedField;
      break;
    }
    
    // Partial matching for complex names
    for (const [key] of Object.entries(networkMapping)) {
      if (normalizedField.includes(key) || key.includes(normalizedField)) {
        detectedNetwork = key;
        break;
      }
    }
    
    if (detectedNetwork !== 'generic') break;
  }

  const iconFile = networkMapping[detectedNetwork] || 'Generic.svg';
  return `/assets/networkFilter/${iconFile}`;
};

// Network name normalization function
// (Removed normalizeNetworkName - now using logo only UI)

const ResponsiveRouteDetailsPanel = ({
  // Core props
  isOpen = false,
  routeData = null,
  waypoints = [],
  routePlannerMode = 'simple', // 'simple' or 'ev_advanced'
  
  // Callbacks
  onClose = () => {},
  onStartNavigation = () => {},
  onSaveTrip = () => {},
  onShareTrip = () => {},
  onAddCheckpoint = () => {},
  onRemoveFromTrip = () => {},
  
  className = ''
}) => {
  // ========================================
  // STATE MANAGEMENT
  // ========================================
  const [showEnergyDetails, setShowEnergyDetails] = useState(false);
  const [expandedClusters, setExpandedClusters] = useState({});
  const [isTripSaved, setIsTripSaved] = useState(false);

  // Mode detection
  const isEVMode = routePlannerMode === 'ev_advanced';
  const theme = useTheme();
  const brandGradient = theme.palette.gradients.brand;
  const brandHoverGradient = theme.palette.gradients.brandHover;
  const brandShadow = theme.palette.mode === 'dark'
    ? '0 20px 45px rgba(2, 6, 23, 0.65)'
    : '0 18px 40px rgba(59, 130, 246, 0.3)';
  const brandSoftSurface = theme.palette.mode === 'dark'
    ? alpha(theme.palette.primary.main, 0.35)
    : alpha(theme.palette.primary.light, 0.15);
  const brandBorder = alpha(theme.palette.primary.main, 0.2);
  const getBatteryGradient = (value) => {
    if (value > 50) return theme.palette.gradients.brand;
    if (value > 20) return theme.palette.gradients.warning;
    return theme.palette.gradients.error;
  };
  const getBatteryColor = (value) => {
    if (value > 50) return theme.palette.primary.main;
    if (value > 20) return theme.palette.warning.main;
    return theme.palette.error.main;
  };
  
  // ========================================
  // DATA PROCESSING
  // ========================================
  
  // Extract chargers based on mode
  const chargersToDisplay = useMemo(() => {
    if (!routeData) return [];
    
    if (isEVMode) {
      return routeData.chargingStops || routeData.chargingStations || [];
    } else {
      return routeData.chargingStations || routeData.chargingStops || [];
    }
  }, [routeData, isEVMode]);
  // (Deprecated local extractKW removed; using extractConnectorPower instead)

  // Extract vehicle and battery data (EV mode only)
  const vehicle = routeData?.selectedVehicle;
  const startingSoC = routeData?.batteryPercentage || 80;
  const currentBattery = routeData?.currentBattery || startingSoC;

  // Calculate summary data
  const summary = useMemo(() => {
    if (!routeData) return null;

    const totalChargeTime = chargersToDisplay.reduce((sum, s) => sum + (s.chargingTime || s.estimatedChargeTime || 0), 0);
    
    let finalSoC = startingSoC;
    let energyNeeded = 0;
    let energyDeficit = 0;

    if (isEVMode) {
      if (routeData.energyPlan) {
        finalSoC = parseFloat(routeData.energyPlan.finalSoC) || startingSoC;
        energyNeeded = routeData.energyPlan.totalEnergyNeeded || 0;
        energyDeficit = routeData.energyPlan.energyDeficit || 0;
      } else if (routeData.energyConsumed) {
        energyNeeded = routeData.energyConsumed || 0;
        finalSoC = routeData.finalSoC || startingSoC;
      }
    }

    return {
      totalDistance: routeData.distance || routeData.route?.totalDistance || 0,
      totalTime: (routeData.duration || routeData.route?.totalDuration || 0) + totalChargeTime,
      drivingTime: routeData.duration || routeData.route?.totalDuration || 0,
      chargingTime: totalChargeTime,
      totalStops: chargersToDisplay.length,
      finalSoC: Math.round(finalSoC),
      startingSoC: Math.round(startingSoC),
      energyNeeded,
      energyDeficit
    };
  }, [routeData, chargersToDisplay, startingSoC, isEVMode]);
  const finalSoCValue = summary?.finalSoC ?? 0;
  const finalBatteryColor = getBatteryColor(finalSoCValue);
  const finalBatteryBackground = alpha(finalBatteryColor, 0.08);
  const finalBatteryBorder = `1px solid ${alpha(finalBatteryColor, 0.25)}`;

  // ========================================
  // DISTANCE + CLUSTER PROCESSING
  // ========================================
  const processedChargerClusters = useMemo(() => {
    if (!chargersToDisplay || chargersToDisplay.length === 0) return [];
    const polyline = routeData?.polyline || [];
    const hasPolyline = Array.isArray(polyline) && polyline.length >= 2;
    const cumulative = hasPolyline ? buildPolylineCumulative(polyline) : [];

    // Determine start for fallback straight-line distance
    let startLat = null, startLng = null;
    if (hasPolyline) {
      startLat = polyline[0].lat; startLng = polyline[0].lng;
    } else if (waypoints?.[0]?.coordinates) {
      startLat = waypoints[0].coordinates.lat || waypoints[0].coordinates[0];
      startLng = waypoints[0].coordinates.lng || waypoints[0].coordinates[1];
    }

    const enriched = chargersToDisplay.map(ch => {
      const lat = parseFloat(ch.locationLatitude || ch.lat || ch.latitude || ch.location?.coordinates?.[1]);
      const lng = parseFloat(ch.locationLongitude || ch.lng || ch.longitude || ch.location?.coordinates?.[0]);
      let distInfo = { distanceFromStartKm: null, offRouteDistanceKm: null, isValid: false };
      if (hasPolyline) {
        distInfo = computeChargerRouteDistance(ch, polyline, cumulative);
      } else if (!hasPolyline && !isNaN(lat) && !isNaN(lng) && startLat != null && startLng != null) {
        // Fallback straight-line from start
        const fallbackDist = haversineKm(startLat, startLng, lat, lng);
        distInfo = { distanceFromStartKm: fallbackDist, offRouteDistanceKm: fallbackDist, isValid: true, isFallback: true };
      }

      // Extract power for primary selection
      let firstConnectorPowerDisplay = null;
      let kwNumeric = null;
      if (Array.isArray(ch.connectors) && ch.connectors.length) {
        firstConnectorPowerDisplay = extractConnectorPower(ch.connectors[0], ch);
        kwNumeric = parseNumericKw(firstConnectorPowerDisplay);
      }
      if (!kwNumeric && (ch.power || ch.maxPower || ch.kw)) {
        kwNumeric = parseFloat(ch.power || ch.maxPower || ch.kw) || null;
      }

      return {
        ...ch,
        _distanceFromStartKm: distInfo.distanceFromStartKm,
        _offRouteDistanceKm: distInfo.offRouteDistanceKm,
        _isDistanceValid: distInfo.isValid,
        _isFallbackDistance: distInfo.isFallback || false,
        _lat: lat,
        _lng: lng,
        _kwNumeric: kwNumeric,
        _firstConnectorPowerDisplay: firstConnectorPowerDisplay
      };
    }).filter(e => e._isDistanceValid);

    const nearRoute = hasPolyline
      ? enriched.filter(e => (e._offRouteDistanceKm == null || e._offRouteDistanceKm <= 2) || e.type === 'charging_stop')
      : enriched; // no filtering when no polyline yet

    const clusters = clusterChargers(nearRoute, 75);

    return clusters.map(cluster => {
      const primary = selectPrimaryCharger(cluster);
      const distanceKm = primary?._distanceFromStartKm;
      const isZero = distanceKm != null && distanceKm <= 0.1;
      return {
        primary,
        members: cluster,
        distanceKm,
        displayDistance: distanceKm == null ? '— km from start' : (isZero ? '0 km from start' : `${distanceKm.toFixed(1)} km from start`),
        isZero,
        offRouteDistanceKm: primary?._offRouteDistanceKm,
        size: cluster.length
      };
    }).sort((a,b) => (a.distanceKm || 0) - (b.distanceKm || 0));
  }, [chargersToDisplay, routeData, waypoints]);

  // ========================================
  // REALISTIC CHARGE TIME CALC
  // ========================================
  // reuse previously declared `vehicle` (earlier summary section) for charge calculations
  const vehicleBatteryCapacity = vehicle?.batteryCapacity || vehicle?.battery_capacity || 60;
  const vehicleFastLimit = vehicle?.fastChargingPower || vehicle?.dcMaxPower || 150;
  const vehicleACLimit = vehicle?.acMaxPower || vehicle?.ac_power || 7.4;

  const computeChargeTimeMinutes = (primary, maxPowerNumeric) => {
    if (!primary) return 0;
    const arrivalSOC = primary.arrivalSoC || primary.arrivalSOC || primary.arrival_soc || primary.arrival ||  (primary._arrivalSoC); // multiple variants
    const departureSOC = primary.departureSoC || primary.departureSOC || primary.targetSoC || 85;
    if (arrivalSOC == null || departureSOC == null) return 0;
    if (departureSOC <= arrivalSOC) return 0;
    if (!vehicleBatteryCapacity) return 0;
    if (!maxPowerNumeric || maxPowerNumeric <= 0) return 0;
    // Determine connector type for power cap
    const isDC = maxPowerNumeric >= 50; // heuristic
    const intakeCap = isDC ? vehicleFastLimit : vehicleACLimit;
    const effectivePower = Math.min(maxPowerNumeric, intakeCap || maxPowerNumeric);
    const socTo80 = Math.max(0, Math.min(80, departureSOC) - arrivalSOC);
    const socAbove80 = Math.max(0, departureSOC - Math.max(80, arrivalSOC));
    const energyTo80 = (socTo80 / 100) * vehicleBatteryCapacity; // kWh
    const energyAbove80 = (socAbove80 / 100) * vehicleBatteryCapacity;
    // Taper: above 80% apply 1.25x time (i.e., divide effective power by 0.8)
    const timeTo80Hr = energyTo80 / effectivePower;
    const timeAbove80Hr = energyAbove80 / (effectivePower * 0.8); // slower
    const totalHr = timeTo80Hr + timeAbove80Hr;
    const minutes = totalHr * 60;
    // Round to nearest 5
    return Math.round(minutes / 5) * 5;
  };

  // ========================================
  // FORMATTING UTILITIES
  // ========================================
  const formatDistance = (km) => {
    if (!km) return '0 km';
    return `${km.toFixed(1)} km`;
  };

  const formatTimeShort = (minutes) => {
    if (!minutes) return '0m';
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const formatEnergy = (kwh) => {
    if (!kwh) return '0 kWh';
    return `${kwh.toFixed(1)} kWh`;
  };

  // ========================================
  // LOCATION NAMES - Enhanced with better fallbacks
  // ========================================
  
  // Enhanced location extraction with multiple fallbacks
  const getLocationName = (waypoint, fallback) => {
    const name = waypoint?.location || 
                 waypoint?.name || 
                 waypoint?.address || 
                 waypoint?.chargerDetails?.station_name ||
                 waypoint?.chargerDetails?.chargepoint_name ||
                 waypoint?.chargerDetails?.name ||
                 waypoint?.locationAddress ||
                 fallback;
    return name;
  };

  const fromLocation = getLocationName(waypoints[0], 'Start Location');
  const toLocation = getLocationName(waypoints[waypoints.length - 1], 'Destination');

  // Handle save trip
  const handleSaveTrip = () => {
    setIsTripSaved(!isTripSaved);
    onSaveTrip();
  };

  // ========================================
  // RENDER GUARD
  // ========================================
  
  if (!isOpen || !routeData) {
    return null;
  }
  
  if (!waypoints || waypoints.length < 2) {
    return null;
  }

  // ========================================
  // DEBUG OUTPUT FOR WAYPOINT DISPLAY
  // ========================================

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <>
      {/* Custom CSS for animations */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>
      
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -50 }}
        transition={{ duration: 0.3 }}
        className={className}
      >
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          zIndex: 2500, // Raised above route planner & map controls
          width: { xs: 'calc(100% - 40px)', sm: 420, md: 440 },
          maxHeight: 'calc(100vh - 40px)',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(16px)',
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* ==================== HEADER ==================== */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'rgba(255, 255, 255, 0.9)'
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight="600" sx={{ color: 'text.primary' }}>
              Trip Details
            </Typography>
            
            {/* Save Trip and Share buttons moved to far right */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={isTripSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                onClick={handleSaveTrip}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  height: 32,
                  minWidth: 'auto',
                  px: 1.5,
                  backgroundImage: isTripSaved ? brandHoverGradient : brandGradient,
                  color: theme.palette.primary.contrastText,
                  boxShadow: brandShadow,
                  '&:hover': {
                    backgroundImage: brandHoverGradient,
                    boxShadow: brandShadow,
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease',
                  border: 'none'
                }}
              >
                {isTripSaved ? 'Saved' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ShareIcon />}
                onClick={onShareTrip}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  height: 32,
                  minWidth: 'auto',
                  px: 1.5,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.05)',
                    borderColor: 'primary.dark',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Share
              </Button>
              <IconButton 
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }} 
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isEVMode ? 'Plan your EV journey' : 'Plan your route'}
          </Typography>
        </Box>

        {/* ==================== SCROLLABLE CONTENT ==================== */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* ==================== ROUTE PLAN SECTION ==================== */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              mb: 3, 
              borderRadius: 3,
              backgroundColor: isEVMode ? 'rgba(33, 150, 243, 0.05)' : 'rgba(76, 175, 80, 0.05)',
              border: `1px solid ${isEVMode ? 'rgba(33, 150, 243, 0.15)' : 'rgba(76, 175, 80, 0.15)'}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <motion.div
                animate={isEVMode ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <BoltIcon sx={{ 
                  color: isEVMode ? 'primary.main' : 'success.main',
                  fontSize: 22,
                  filter: isEVMode ? 'drop-shadow(0 2px 4px rgba(33, 150, 243, 0.3))' : 'none'
                }} />
              </motion.div>
              <Typography variant="subtitle1" fontWeight="600">
                {isEVMode ? 'Trip Plan' : 'Route Plan'}
              </Typography>
              {isEVMode && (
                <Chip
                  label="EV"
                  size="small"
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 20,
                    animation: 'pulse 2s infinite'
                  }}
                />
              )}
            </Stack>

            {/* Route Waypoints */}
            <Stack spacing={1.5}>
              {/* FROM */}
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25
                  }}
                >
                  <Typography variant="body2" fontWeight="600" color="white">
                    A
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                    FROM
                  </Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ 
                    lineHeight: 1.3,
                    wordBreak: 'break-word'
                  }}>
                    {fromLocation}
                  </Typography>
                </Box>
              </Stack>

              {/* CHECKPOINTS (if any) */}
              {waypoints.slice(1, -1).map((checkpoint, index) => (
                <Stack key={checkpoint.id} direction="row" alignItems="flex-start" spacing={2}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'warning.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.25
                    }}
                  >
                    <Typography variant="body2" fontWeight="600" color="white">
                      {String.fromCharCode(66 + index)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                      CHECKPOINT
                    </Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ 
                      lineHeight: 1.3,
                      wordBreak: 'break-word'
                    }}>
                      {getLocationName(checkpoint, `Checkpoint ${index + 1}`)}
                    </Typography>
                  </Box>
                  {/* Remove checkpoint button */}
                  <motion.div
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        onRemoveFromTrip(index + 1);
                      }}
                      sx={{
                        ml: 1,
                        px: 1.6,
                        py: 0.4,
                        minWidth: 'auto',
                        borderRadius: 2,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        background: 'linear-gradient(135deg, #f1472dff 0%, #d04320ff 100%)',
                        color: '#fff',
                        boxShadow: '0 3px 10px rgba(244,67,54,0.35)',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
                          boxShadow: '0 6px 16px rgba(244,67,54,0.45)',
                        },
                        '&:active': {
                          boxShadow: '0 3px 10px rgba(244,67,54,0.35)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-120%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
                          transform: 'skewX(-20deg)',
                          transition: 'left 0.6s ease'
                        },
                        '&:hover::before': {
                          left: '120%'
                        }
                      }}
                    >
                      Remove trip
                    </Button>
                  </motion.div>
                  </Stack>
                  ))}

                  {/* TO */}
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25
                  }}
                >
                  <Typography variant="body2" fontWeight="600" color="white">
                    {waypoints.length > 2 ? String.fromCharCode(65 + waypoints.length - 1) : 'B'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                    TO
                  </Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ 
                    lineHeight: 1.3,
                    wordBreak: 'break-word'
                  }}>
                    {toLocation}
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {/* START NAVIGATION BUTTON - Within Trip Plan */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<NavigationIcon />}
              onClick={onStartNavigation}
              sx={{
                mt: 2.5,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 700,
                background: brandGradient,
                boxShadow: brandShadow,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  transition: 'left 0.5s ease',
                },
                '&:hover': {
                  background: brandHoverGradient,
                  boxShadow: '0 24px 45px rgba(37, 99, 235, 0.35)',
                  transform: 'translateY(-3px)',
                  '&::before': {
                    left: '100%',
                  }
                },
                '&:active': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 14px 30px rgba(37, 99, 235, 0.35)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Start Navigation
            </Button>
          </Paper>

          {/* ==================== EV VEHICLE INFO (EV MODE ONLY) ==================== */}
          {isEVMode && vehicle && (
            <Paper 
              elevation={0}
              sx={{ 
                p: 2.5, 
                mb: 3, 
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'grey.100',
                    border: '2px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  <ElectricCarIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    {vehicle.make} {vehicle.model}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Range: {vehicle.range}km • Battery: {vehicle.batteryCapacity}kWh
                  </Typography>
                </Box>
              </Stack>

              {/* Current Battery Status */}
              <Box sx={{ mt: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current Battery
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="primary.main">
                    {currentBattery}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={currentBattery}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: alpha(theme.palette.text.primary, 0.08),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundImage: getBatteryGradient(currentBattery),
                      backgroundColor: getBatteryColor(currentBattery)
                    }
                  }}
                />
              </Box>
            </Paper>
          )}

          {/* ==================== DISTANCE & TIME CARDS ==================== */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 3,
                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                border: '1px solid rgba(33, 150, 243, 0.15)',
                textAlign: 'center'
              }}
            >
              <RouteIcon sx={{ color: 'primary.main', fontSize: 28, mb: 1 }} />
              <Typography variant="h5" fontWeight="700" color="primary.main">
                {formatDistance(summary?.totalDistance)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Distance
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 3,
                backgroundColor: 'rgba(156, 39, 176, 0.05)',
                border: '1px solid rgba(156, 39, 176, 0.15)',
                textAlign: 'center'
              }}
            >
              <TimerIcon sx={{ color: 'secondary.main', fontSize: 28, mb: 1 }} />
              <Typography variant="h5" fontWeight="700" color="secondary.main">
                {formatTimeShort(summary?.totalTime)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Trip Time
              </Typography>
            </Paper>
          </Stack>

          {/* ==================== EV ENERGY SECTION ==================== */}
          {isEVMode && (
            <>
              {/* Energy Cards Row */}
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 193, 7, 0.05)',
                    border: '1px solid rgba(255, 193, 7, 0.15)',
                    textAlign: 'center'
                  }}
                >
                  <BoltIcon sx={{ color: 'warning.main', fontSize: 28, mb: 1 }} />
                  <Typography variant="h5" fontWeight="700" color="warning.main">
                    {summary?.totalStops || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Charging Stops
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: finalBatteryBackground,
                    border: finalBatteryBorder,
                    textAlign: 'center'
                  }}
                >
                  <Battery80Icon sx={{ 
                    color: finalBatteryColor, 
                    fontSize: 28, 
                    mb: 1 
                  }} />
                  <Typography variant="h5" fontWeight="700" sx={{
                    color: finalBatteryColor
                  }}>
                    {summary?.finalSoC}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Final Battery
                  </Typography>
                </Paper>
              </Stack>

              {/* Battery & Energy Toggle Section */}
              <Paper 
                elevation={0}
                sx={{ 
                  mb: 3, 
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <BatteryIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight="600">
                        Battery & Energy
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        Show Details
                      </Typography>
                      <Switch
                        checked={showEnergyDetails}
                        onChange={(e) => setShowEnergyDetails(e.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-thumb': {
                            backgroundColor: showEnergyDetails ? 'success.main' : 'grey.400'
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: showEnergyDetails ? 'success.light' : 'grey.300'
                          }
                        }}
                      />
                    </Stack>
                  </Stack>
                </Box>

                <Collapse in={showEnergyDetails}>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Consumption
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {formatEnergy(summary?.energyNeeded)}
                        </Typography>
                      </Stack>
                      
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Est. Range
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {vehicle?.range || 140} km
                        </Typography>
                      </Stack>
                      
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Start Battery
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {summary?.startingSoC}%
                        </Typography>
                      </Stack>
                      
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Final Battery Level
                        </Typography>
                        <Typography variant="body1" fontWeight="500" sx={{
                          color: summary?.finalSoC > 20 ? 'success.main' : 'error.main'
                        }}>
                          {summary?.finalSoC}%
                        </Typography>
                      </Stack>

                      {/* Battery Level Progress */}
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={summary?.finalSoC}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: summary?.finalSoC > 50 
                                ? 'linear-gradient(90deg, #4CAF50 0%, #81C784 100%)'
                                : summary?.finalSoC > 20
                                ? 'linear-gradient(90deg, #FF9800 0%, #FFB74D 100%)'
                                : 'linear-gradient(90deg, #F44336 0%, #EF5350 100%)'
                            }
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                </Collapse>
              </Paper>
            </>
          )}

          {/* ==================== CHARGING STOP PREVIEW SECTION ==================== */}
          {processedChargerClusters.length > 0 && (
            <Paper 
              elevation={0}
              sx={{ 
                mb: 3, 
                borderRadius: 3,
                backgroundImage: `linear-gradient(135deg, ${brandSoftSurface}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${brandBorder}`,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25)'
              }}
            >
              <Box sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <BoltIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="600" color={theme.palette.primary.main}>
                    Charging Stop Preview
                  </Typography>
                </Stack>
                <Typography variant="body2" color={theme.palette.primary.main} fontWeight="600" sx={{ mb: 3 }}>
                  {processedChargerClusters.length} locations planned
                </Typography>

                <Stack spacing={2}>
                  {processedChargerClusters.map((cluster, index) => {
                    const { primary, members, size, displayDistance } = cluster;
                    const networkIcon = getNetworkIcon(primary);
                    // Power extraction (fastest connector among primary if multiple)
                    let powerDisplay = '—';
                    let maxPowerNumeric = 0;
                    if (Array.isArray(primary?.connectors) && primary.connectors.length) {
                      primary.connectors.forEach(conn => {
                        const disp = extractConnectorPower(conn, primary);
                        const num = parseNumericKw(disp) || 0;
                        if (num > maxPowerNumeric) {
                          maxPowerNumeric = num;
                          powerDisplay = disp.replace(/\s+DC|\s+AC/i, ''); // simplify to just kW
                        }
                      });
                    } else if (primary?._kwNumeric) {
                      maxPowerNumeric = primary._kwNumeric;
                      powerDisplay = `${maxPowerNumeric}kW`;
                    }

                    // Speed tag removed per latest request
                    const chargingTimeMinutesRaw = computeChargeTimeMinutes(primary, maxPowerNumeric) || primary?.chargingTime || primary?.estimatedChargeTime || 0;
                    const roundedTime = chargingTimeMinutesRaw; // already rounded in compute
                    const timeDisplay = roundedTime > 0 ? `\u23F1 ${roundedTime} min` : 'n/a';

                    // Address extraction
                    const rawAddress = primary?.address || primary?.locationAddress || primary?.formatted_address || primary?.location?.address || primary?.location?.formatted_address || primary?.street || primary?.location?.street;
                    const address = rawAddress ? String(rawAddress) : '';

                    return (
                      <Paper
                        key={primary?.id || `cluster-${index}`}
                        elevation={0}
                        sx={{
                          p: 2.2,
                          borderRadius: 3,
                          backgroundColor: 'rgba(255,255,255,0.96)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          position: 'relative',
                          '&:hover': {
                            backgroundColor: 'white',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all .25s ease'
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          {/* Number badge */}
                          <Box sx={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            backgroundImage: brandGradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: brandShadow
                          }}>
                            <Typography variant="h6" fontWeight={700} color={theme.palette.primary.contrastText}>{index + 1}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.25, fontSize: '1.05rem', lineHeight: 1.2 }}>
                              {primary?.name || primary?.chargepoint_name || primary?.station_name || `Location ${index + 1}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <NavigationIcon sx={{ fontSize: 14, mr: .5 }} />
                              {displayDistance}
                            </Typography>
                            {address && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, opacity: .75, fontSize: '.62rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {address}
                              </Typography>
                            )}
                            {/* Single info row (power • time); speed tag removed per latest request */}
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.25, flexWrap: 'wrap' }}>
                              <Typography variant="body1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center' }}>
                                {powerDisplay || '— kW'}
                              </Typography>
                              <Typography variant="body2" fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                                {timeDisplay}
                              </Typography>
                              {size > 1 && (
                                <Chip onClick={() => setExpandedClusters(prev => ({ ...prev, [index]: !prev[index] }))} label={`${expandedClusters[index] ? 'Hide' : '+' + (size - 1) + ' more'}`} size="small" color="success" variant="outlined" sx={{ fontSize: '.6rem', height: 20, fontWeight: 600, cursor: 'pointer' }} />
                              )}
                            </Stack>
                            {/* Network logo only */}
                            <Box component="img" src={networkIcon} alt="network" onError={e => { e.target.src = '/assets/networkFilter/Generic.svg'; }}
                              sx={{ width: 40, height: 26, objectFit: 'contain', backgroundColor: 'white', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1, p: .5 }} />
                          </Box>
                        </Stack>
                        {/* Expand hidden members list */}
                        {size > 1 && expandedClusters[index] && (
                          <Box sx={{ mt: 1 }}>
                            <Collapse in={expandedClusters[index]}>
                              <Stack spacing={1} sx={{ mt: 1 }}>
                                {members.slice(1).map((m, mi) => (
                                  <Typography key={m.id || mi} variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    • {m.name || m.chargepoint_name || `Variant ${mi + 2}`} ({m._kwNumeric ? `${m._kwNumeric}kW` : '— kW'})
                                  </Typography>
                                ))}
                              </Stack>
                            </Collapse>
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            </Paper>
          )}

          {/* Note: Start Navigation button is now integrated within Trip Plan section */}
        </Box>
      </Box>
      </motion.div>
    </>
  );
};

export default ResponsiveRouteDetailsPanel;